/**
 * Aba Atividade enriquecida — comentarios, responsavel, tipo de tarefa,
 * toolbar inline e botao "Atribuir tarefa" (cria subtarefa ou comment).
 *
 * v2: threading (1 nivel), kebab menu, privado funcional, anexos no comentario,
 * historico colapsavel (3 threads), linkify.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Lock, CalendarDays, Clock, AlertTriangle, User, ChevronDown, ChevronUp } from 'lucide-react';
import { GlassDatePicker } from '@/components/ui/glass-date-picker';
import { GlassTimePicker } from '@/components/ui/glass-time-picker';
import { useState, useRef, useEffect } from 'react';

import {
  apiClient,
  type TaskComment,
  type TaskCategory,
  type MembershipWithUserResponse,
  type CreateTaskInput,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { MentionEditor, type MentionMember } from './mention-editor';
import { TaskCommentItem } from './task-comment-item';
import { TaskCommentAttachmentPicker } from './task-comment-attachment-picker';

// ---------------------------------------------------------------------------
// Helpers locais
// ---------------------------------------------------------------------------

function relativeDate(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { locale: ptBR, addSuffix: true });
  } catch {
    return iso;
  }
}

/** Agrupa comentários em threads (root + seus replies) */
function buildThreads(comments: TaskComment[]): { root: TaskComment; replies: TaskComment[] }[] {
  const roots = comments.filter((c) => c.parentCommentId === null);
  return roots.map((root) => ({
    root,
    replies: comments.filter((c) => c.parentCommentId === root.id),
  }));
}

// ---------------------------------------------------------------------------
// Toast simples inline
// ---------------------------------------------------------------------------

function useToast() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 2500);
    return () => clearTimeout(t);
  }, [msg]);
  return { msg, toast: setMsg };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActivityTabProps {
  taskId: string;
  /** tenantId para buscar membros */
  tenantId: string;
  /** clientId para pré-popular "Para" no encaminhar */
  clientId?: string | null;
  /** caseId para vincular subtarefa */
  caseId?: string | null;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function ActivityTab({ taskId, tenantId, clientId, caseId }: ActivityTabProps) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const { msg: toastMsg, toast } = useToast();

  // Comentários existentes
  const { data: comments = [], isLoading } = useQuery<TaskComment[]>({
    queryKey: ['task', taskId, 'comments'],
    queryFn: () => apiClient.tasksListComments(taskId),
    staleTime: 30_000,
  });

  // Membros do tenant para @-mention e dropdown responsáveis
  const { data: membersData = [] } = useQuery<MembershipWithUserResponse[]>({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => apiClient.tenantsListMembers(tenantId),
    staleTime: 5 * 60_000,
    enabled: !!tenantId,
  });

  // Categorias de tarefa
  const { data: categories = [] } = useQuery<TaskCategory[]>({
    queryKey: ['task-categories'],
    queryFn: () => apiClient.taskCategoriesList(),
    staleTime: 5 * 60_000,
  });

  const mentionMembers: MentionMember[] = membersData.map((m) => ({
    id: m.userId,
    name: m.user.name,
  }));

  // Form state
  const [newContent, setNewContent] = useState('');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [fatalDate, setFatalDate] = useState('');
  const [isPrivateComment, setIsPrivateComment] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const assigneeRef = useRef<HTMLDivElement>(null);

  // Histórico colapsável
  const [expanded, setExpanded] = useState(false);

  const invalidateComments = () =>
    queryClient.invalidateQueries({ queryKey: ['task', taskId, 'comments'] });

  const invalidateTask = () =>
    queryClient.invalidateQueries({ queryKey: ['task', taskId] });

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target as Node)) {
        setAssigneeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Mutações
  const addCommentMut = useMutation({
    mutationFn: (input: { content: string; isPrivate: boolean }) =>
      apiClient.tasksAddComment(taskId, { content: input.content, isPrivate: input.isPrivate }),
    onSuccess: async (comment) => {
      // Upload de anexos em paralelo se houver
      if (pendingFiles.length > 0) {
        await Promise.all(
          pendingFiles.map((f) =>
            apiClient.tasksUploadCommentAttachment(taskId, comment.id, f).catch(() => null),
          ),
        );
        setPendingFiles([]);
      }
      setNewContent('');
      setIsPrivateComment(false);
      void invalidateComments();
    },
  });

  const createSubtaskMut = useMutation({
    mutationFn: (input: CreateTaskInput) => apiClient.tasksCreate(input),
    onSuccess: () => {
      setNewContent('');
      setSelectedAssigneeIds([]);
      setSelectedCategoryId('');
      setDueDate('');
      setDueTime('');
      setFatalDate('');
      void invalidateTask();
      void invalidateComments();
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newContent.replace(/<[^>]*>/g, '').trim();
    const hasAssignees = selectedAssigneeIds.length > 0;
    const hasDate = !!dueDate;
    const hasCategory = !!selectedCategoryId;

    // Se tem responsável + data + categoria → cria subtarefa
    if (hasAssignees && hasDate && hasCategory) {
      const input: CreateTaskInput = {
        title: trimmed || 'Subtarefa',
        description: newContent || null,
        categoryId: selectedCategoryId,
        assigneeUserIds: selectedAssigneeIds,
        dueDate,
        dueTime: dueTime || null,
        fatalDate: fatalDate || null,
        caseId: caseId ?? null,
        clientId: clientId ?? null,
        parentTaskId: taskId,
        allDay: !dueTime,
        showInAgenda: false,
      };
      createSubtaskMut.mutate(input);
    } else {
      // Cria só o comentário
      if (!newContent.replace(/<[^>]*>/g, '').trim()) return;
      addCommentMut.mutate({ content: newContent, isPrivate: isPrivateComment });
    }
  }

  function toggleAssignee(userId: string) {
    setSelectedAssigneeIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  const isPending = addCommentMut.isPending || createSubtaskMut.isPending;
  const selectedAssigneeNames = selectedAssigneeIds
    .map((id) => membersData.find((m) => m.userId === id)?.user.name)
    .filter(Boolean);

  // Threading
  const threads = buildThreads(comments);
  const COLLAPSE_COUNT = 3;
  const visibleThreads = expanded || threads.length <= COLLAPSE_COUNT
    ? threads
    : threads.slice(-COLLAPSE_COUNT); // 3 mais recentes

  return (
    <div className="flex flex-col gap-4 px-5 py-4">
      {/* Toast inline */}
      {toastMsg && (
        <div className="rounded-md bg-[color-mix(in_oklch,var(--accent)_15%,transparent)] border border-[var(--accent)] px-3 py-1.5 text-xs text-[var(--accent)] text-center">
          {toastMsg}
        </div>
      )}

      {/* Linha Responsável */}
      <div className="flex items-center gap-2 relative" ref={assigneeRef}>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--accent)_15%,transparent)] text-[var(--accent)]">
          <User className="h-3.5 w-3.5" />
        </div>
        <button
          type="button"
          onClick={() => setAssigneeDropdownOpen((v) => !v)}
          className="flex items-center gap-1 rounded border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1 text-xs text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
        >
          {selectedAssigneeNames.length > 0
            ? selectedAssigneeNames.join(', ')
            : 'Adicionar responsável'}
          <ChevronDown className="h-3 w-3 text-[var(--fg-muted)]" />
        </button>
        {assigneeDropdownOpen && (
          <div className="absolute top-8 left-0 z-50 w-56 rounded-md border border-[var(--border)] bg-[var(--card-bg)] shadow-lg overflow-hidden">
            {membersData.map((m) => (
              <button
                key={m.userId}
                type="button"
                onClick={() => toggleAssignee(m.userId)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm transition-colors',
                  selectedAssigneeIds.includes(m.userId)
                    ? 'bg-blue-600 text-white'
                    : 'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                )}
              >
                {m.user.name}
              </button>
            ))}
            {membersData.length === 0 && (
              <p className="px-3 py-2 text-xs text-[var(--fg-muted)]">Nenhum membro</p>
            )}
          </div>
        )}
      </div>

      {/* Linha Tipo (categoria) */}
      <div className="flex items-center gap-2">
        <select
          value={selectedCategoryId}
          onChange={(e) => setSelectedCategoryId(e.target.value)}
          className="rounded border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1 text-xs text-[var(--fg)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
        >
          <option value="">Tipo de tarefa (opcional)</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Editor de comentário */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <MentionEditor
          value={newContent}
          onChange={setNewContent}
          members={mentionMembers}
          placeholder="Escreva um comentário ou descrição da subtarefa…"
          rows={3}
        />

        {/* Toolbar inline */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Anexo */}
          <TaskCommentAttachmentPicker files={pendingFiles} onChange={setPendingFiles} />

          {/* Privado toggle */}
          <button
            type="button"
            title={isPrivateComment ? 'Privado (clique para desativar)' : 'Marcar como privado'}
            onClick={() => setIsPrivateComment((v) => !v)}
            className={cn(
              'flex items-center gap-1 rounded px-2 py-1 text-[11px] border transition',
              isPrivateComment
                ? 'border-purple-400 bg-purple-100 text-purple-700'
                : 'border-[var(--border)] text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
            )}
          >
            <Lock className="h-3 w-3" />
            Privado
          </button>

          {/* Data */}
          <div className="flex items-center gap-1" title="Data de vencimento">
            <CalendarDays className="h-3 w-3 shrink-0 text-[var(--fg-muted)]" />
            <div className="w-32">
              <GlassDatePicker
                value={dueDate}
                onChange={setDueDate}
                placeholder="Data"
              />
            </div>
          </div>

          {/* Hora */}
          <div className="flex items-center gap-1" title="Hora">
            <Clock className="h-3 w-3 shrink-0 text-[var(--fg-muted)]" />
            <div className="w-24">
              <GlassTimePicker
                value={dueTime}
                onChange={setDueTime}
                placeholder="Hora"
              />
            </div>
          </div>

          {/* Prazo fatal */}
          <div className="flex items-center gap-1" title="Prazo fatal">
            <AlertTriangle className="h-3 w-3 shrink-0 text-red-500" />
            <div className="w-32">
              <GlassDatePicker
                value={fatalDate}
                onChange={setFatalDate}
                placeholder="Prazo fatal"
              />
            </div>
          </div>
        </div>

        {/* Botão principal */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isPending}
            className="rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40 transition"
          >
            {isPending
              ? 'Enviando…'
              : selectedAssigneeIds.length > 0 && dueDate && selectedCategoryId
                ? 'Atribuir tarefa'
                : 'Comentar'}
          </button>
        </div>

        {/* Dica de subtarefa */}
        {(selectedAssigneeIds.length > 0 || dueDate || selectedCategoryId) && (
          <p className="text-[11px] text-[var(--fg-muted)]">
            {selectedAssigneeIds.length > 0 && dueDate && selectedCategoryId
              ? 'Preencha responsável + data + tipo para criar subtarefa; caso contrário só cria comentário.'
              : 'Preencha responsável, data e tipo de tarefa para criar subtarefa.'}
          </p>
        )}
      </form>

      {/* Lista de comentários */}
      {isLoading && (
        <div className="flex flex-col gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="h-8 w-8 rounded-full bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-24 rounded bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]" />
                <div className="h-3 w-48 rounded bg-[color-mix(in_oklch,var(--fg)_5%,transparent)]" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && comments.length === 0 && (
        <p className="text-center text-sm text-[var(--fg-muted)]">
          Nenhum comentário ainda.
        </p>
      )}

      {/* Header colapsável quando > 3 threads */}
      {!isLoading && threads.length > COLLAPSE_COUNT && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
        >
          <ChevronDown className="h-3.5 w-3.5" />
          Mostrar todos os {threads.length} comentários
        </button>
      )}
      {!isLoading && threads.length > COLLAPSE_COUNT && expanded && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
        >
          <ChevronUp className="h-3.5 w-3.5" />
          Mostrar menos
        </button>
      )}

      {/* Threads */}
      <div className="flex flex-col gap-4">
        {visibleThreads.map(({ root, replies }) => (
          <TaskCommentItem
            key={root.id}
            comment={root}
            replies={replies}
            taskId={taskId}
            members={mentionMembers}
            isRoot
            onToast={toast}
          />
        ))}
      </div>
    </div>
  );
}
